import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';

export function SelfHostingPage() {
  return (
    <DocsLayout
      title="Self-hosting"
      updated="May 7, 2026"
      summary="Rynora ships as Docker images you can run anywhere. The reference deployment is a single Oracle Cloud Always Free VM — but the same containers run on AWS, GCP, Azure, or your laptop."
    >
      <h2>What you get</h2>
      <ul>
        <li>
          <strong>API</strong> — auth, flows, runs, billing. Port <code>3001</code>.
        </li>
        <li>
          <strong>MVP runner</strong> — Playwright + browser orchestration. Port <code>4400</code>.
        </li>
        <li>
          <strong>API runner</strong> — HTTP-only test runner. Port <code>4500</code>.
        </li>
        <li>
          <strong>Run orchestrator</strong> — fan-out / queueing. Port <code>4600</code>.
        </li>
        <li>
          <strong>Flow builder</strong> — the drag-and-drop UI. Port <code>3100</code>.
        </li>
        <li>
          <strong>Landing page</strong> — what you're reading right now. Port <code>3000</code>.
        </li>
        <li>
          <strong>Admin console</strong> — deployments, logs, settings. Port <code>3200</code>.
        </li>
        <li>
          <strong>Postgres</strong> — database. Port <code>5432</code>.
        </li>
      </ul>

      <h2>Reference deployment</h2>
      <p>
        We run all of the above on a single Oracle Cloud Always Free instance (24GB RAM, 4 OCPU). Caddy handles TLS,
        Cloudflare handles DNS + WAF, Backblaze B2 handles backups. Total recurring cost: <strong>$0/month</strong>. The
        full runbook is in <code>docs/operations-guide.md</code> in the repo.
      </p>

      <h2>One-machine quickstart</h2>
      <p>For a single QA-grade environment on a fresh Linux box:</p>
      <pre className="docs-codeblock">{`git clone https://github.com/rynoratech/rynora.git
cd rynora
cp .env.qa.example .env.qa

# Edit .env.qa — at minimum set:
#   POSTGRES_PASSWORD=<long random>
#   JWT_SECRET=<long random>
#   ADMIN_EMAIL=<your email>

docker compose -f docker-compose.qa.yml up -d
docker compose -f docker-compose.qa.yml exec api npm run migrate

# Browse to http://<host>:13000`}</pre>

      <h2>Three-environment deployment</h2>
      <p>
        For QA + UAT + Production on the same host (the recommended layout for a small team), each environment binds to
        its own ports and Docker network:
      </p>
      <table>
        <thead>
          <tr>
            <th>Env</th>
            <th>Port range</th>
            <th>Compose file</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>QA</td>
            <td>13xxx</td>
            <td>
              <code>docker-compose.qa.yml</code>
            </td>
          </tr>
          <tr>
            <td>UAT</td>
            <td>14xxx</td>
            <td>
              <code>docker-compose.uat.yml</code>
            </td>
          </tr>
          <tr>
            <td>Production</td>
            <td>15xxx</td>
            <td>
              <code>docker-compose.prod.yml</code>
            </td>
          </tr>
          <tr>
            <td>Admin console</td>
            <td>16xxx</td>
            <td>
              <code>docker-compose.admin.yml</code>
            </td>
          </tr>
        </tbody>
      </table>
      <p>
        Caddy maps <code>qa.rynora.app</code>, <code>uat.rynora.app</code>, <code>rynora.app</code>, and{' '}
        <code>admin.rynora.app</code> to those ports. The example Caddyfile is in the repo at{' '}
        <code>Caddyfile.example</code>.
      </p>

      <h2>Hardening</h2>
      <p>
        Set <code>NODE_ENV=production</code> or <code>NODE_ENV=uat</code> to enable hardened mode. That:
      </p>
      <ul>
        <li>Enforces all required environment variables — fails fast on missing secrets.</li>
        <li>Turns on a strict CSP and HSTS via Helmet.</li>
        <li>Disables Swagger UI on the API (the JSON spec is still available behind admin auth).</li>
        <li>Forces HTTPS on every webhook URL.</li>
      </ul>

      <h2>Backups</h2>
      <p>
        Run <code>scripts/backup.sh</code> on a daily cron. It runs <code>pg_dump</code>, encrypts with age, and uploads
        to Backblaze B2. <code>scripts/restore.sh</code> reverses it. We rehearse restores weekly.
      </p>

      <h2>Updates</h2>
      <p>
        New images land on GHCR every push to <code>main</code>. Trigger a deploy from <code>admin.rynora.app</code> →{' '}
        <strong>Deployments</strong> → pick the env. The button runs the GitHub Actions workflow in{' '}
        <code>.github/workflows/deploy.yml</code>.
      </p>

      <h2>Need help?</h2>
      <p>
        Open an issue on{' '}
        <a href="https://github.com/rynoratech/rynora/issues" target="_blank" rel="noreferrer">
          GitHub
        </a>{' '}
        or email <a href="mailto:support@rynora.app">support@rynora.app</a>. For a paid deployment review, hit the{' '}
        <Link to="/contact">contact form</Link> with topic <em>Sales</em>.
      </p>
    </DocsLayout>
  );
}
